import React, { createContext, useContext, useState, useEffect } from "react";
import useGenerateProcess from "hooks/api/process/useGenerateProcess";
import useGetProcessResult from "hooks/api/process/useGetProcessResult";
import useProcessStatusPolling from "hooks/api/process/useProcessStatusPolling";
import GlobalMira from "elements/GlobalMira";

/**
 * Context for managing global Mira functionality.
 * Provides a hook to access the `showMira` function and other stateful operations.
 */
const MiraContext = createContext();

/**
 * Custom hook to access the global Mira functionality.
 * @returns {Object} - An object containing `showMira`, `hideMira`, `generate`, and `miraState`.
 * @throws {Error} - If the hook is used outside of a `GlobalMiraProvider`.
 */
export const useMira = () => {
  const context = useContext(MiraContext);

  if (!context) {
    throw new Error("useMira must be used within a MiraProvider");
  }

  // Cleanup function to reset Mira state when unmounted
  useEffect(() => {
    return () => {
      context.setMiraReadyState();
    };
  }, []);

  const { showMira, hideMira, generate, miraState, setMiraReadyState } = context;
  return { showMira, hideMira, generate, miraState, setMiraReadyState };
};

/**
 * Provider component to manage global Mira state and provide functionality to child components.
 * @param {Object} props - Component props.
 * @param {React.ReactNode} props.children - Child components.
 */
export const GlobalMiraProvider = ({ children }) => {
  // Default UI properties for GlobalMira
  const defaultProps = {
    open: false,
    retryHandler: null,
    userInput: "",
    buttonText: "Generate",
    copyToClipboard: true,
    customButton: null,
    showTextArea: true,
    showOutputArea: true,
  };

  // Initial Mira state configuration enabling the functionality to make api calls
  const miraReadyState = {
    processIsComplete: false,
    processStatus: "ready",
    response: "",
    invocationId: null,
    error: false,
    targetProcess: "",
    processData: null,
  };
  // State to manage GlobalMira UI properties
  const [globalMiraProps, setGlobalMiraProps] = useState({
    ...defaultProps,
  });

  // Separate state to manage Mira's API interactions and processing status
  const [miraState, setMiraState] = useState({
    ...miraReadyState,
  });

  /**
   * Opens the Mira modal with customizable properties.
   * @param {Object} options - Configuration options for GlobalMira UI.
   * @param {Function|null} [options.retryHandler=null] - Function to retry the action in case of an error.
   * @param {string} [options.userInput=""] - User input to display in the input text box.
   * @param {string} [options.buttonText="Generate"] - Text displayed on the action button in GlobalMira.
   * @param {boolean} [options.copyToClipboard=true] - Whether to show the Copy to Clipboard button.
   * @param {React.ReactNode|null} [options.customButton=null] - Custom button component that overrides the default button.
   * @param {boolean} [options.showTextArea=true] - Whether to display the input text area.
   * @param {boolean} [options.showOutputArea=true] - Whether to display the response area.
   */
  const showMira = ({
    retryHandler = null,
    userInput = "",
    buttonText = "Generate",
    copyToClipboard = true,
    customButton = null,
    showTextArea = true,
    showOutputArea = true,
  }) => {
    setGlobalMiraProps({
      open: true,
      retryHandler,
      userInput,
      buttonText,
      copyToClipboard,
      customButton,
      showTextArea,
      showOutputArea,
    });
  };

  /**
   * Closes the Mira modal and resets its state.
   */
  const hideMira = () => {
    setGlobalMiraProps({
      ...defaultProps,
      open: false,
    });

    setMiraState({
      ...miraReadyState,
    });
  };

  /**
   * Resets Mira state to its ready state.
   */
  const setMiraReadyState = () => {
    setMiraState(() => ({
      ...miraReadyState,
    }));
  };

  /**
   * Initiates process generation by setting the required parameters.
   * @param {Object} processData - Data related to the target process.
   * @param {string} processData.targetProcess - The type of process to generate.
   * @param {Object} processData.platformContext - Context data required for the process.
   * @throws {Error} - If input parameters are invalid.
   */
  const generate = (processData) => {
    const { targetProcess, platformContext } = processData;
    if (!targetProcess || !platformContext) {
      throw new Error("Invalid input: targetProcess and platformContext are required.");
    }
    if (miraState.processStatus != "ready") {
      console.error("Generate already in process");
      return;
    }

    setMiraState((prev) => ({
      ...prev,
      processStatus: "in_progress",
      targetProcess,
      error: false,
      processData, //enables useGenerateProcess
    }));
  };

  useGenerateProcess({
    enabled: miraState.targetProcess != "" && miraState.invocationId === null,
    processData: miraState.processData,
    onSuccess: ({ data }) => {
      setTimeout(() => {
        setMiraState((prevProps) => ({
          ...prevProps,
          invocationId: data?.invocation_id,
        }));
      }, 4000); //Timeout to give step function time to run before activating polling
    },
    onError: (error) => {
      console.error("Generate Process error:", error);
      setMiraState(() => ({
        ...miraReadyState, // reset ready state to enable a subsequent generate() call if necessary after an error
        error: "An error occurred during process generation.",
      }));
    },
  });

  // Polls for the process status and updates the state accordingly.
  useProcessStatusPolling({
    invocationId: miraState.invocationId,
    onSuccess: ({ data }) => {
      if (data?.status === "completed") {
        setMiraState((prevProps) => ({
          ...prevProps,
          processIsComplete: true, // Enables useGetProcess
        }));
      } else if (data?.status === "failed") {
        const error = data?.metadata?.errorMessage || "Unknown error";
        console.error("Async process error occured: ", error);
        setMiraState(() => ({
          ...miraReadyState,
          error: true,
        }));
      }
    },
    onError: (error) => {
      console.error("Polling error:", error);
      setMiraState(() => ({
        ...miraReadyState,
        error: true,
      }));
    },
  });

  // retrieves the process result once polling is complete
  useGetProcessResult({
    invocationId: miraState.invocationId,
    enabled: miraState.processIsComplete,
    targetProcess: miraState.targetProcess,
    onSuccess: async ({ data }) => {
      if (miraState.processData.platformContext.responseType === "zip") {
        let blobData = new Blob([data], { type: "application/zip" });

        const downloadLink = document.createElement("a");
        const url = URL.createObjectURL(blobData);

        downloadLink.href = url;
        downloadLink.download = "session-notes.zip";
        downloadLink.style.display = "none";

        document.body.appendChild(downloadLink);
        downloadLink.click();

        setTimeout(() => {
          document.body.removeChild(downloadLink);
          URL.revokeObjectURL(url);
        }, 200);
        setMiraState(() => ({
          ...miraReadyState,
          response: "zip_file_download",
        }));
      } else {
        setMiraState(() => ({
          ...miraReadyState,
          response: data,
        }));
      }
    },

    onError: (error) => {
      console.error("Get Result error:", error);
      setMiraState(() => ({
        ...miraReadyState, // reset ready state to enable a subsequent generate() call if necessary after an error
        error: true,
      }));
    },
  });

  /**
   * Updates user input in GlobalMira component.
   * @param {string} input - The user input.
   */
  const handleUserInput = (input) => {
    setGlobalMiraProps((prevProps) => ({
      ...prevProps,
      userInput: input,
    }));
  };

  /**
   * Allows users to modify the response content.
   * @param {string} input - The modified response.
   */
  const handleOutputChange = (input) => {
    setMiraState((prevProps) => ({
      ...prevProps,
      response: input,
    }));
  };

  // Render the Provider with the mira context value and the GlobalMira component
  return (
    <MiraContext.Provider
      value={{
        showMira,
        hideMira,
        generate,
        miraState,
        setMiraReadyState,
      }}
    >
      {children}
      <GlobalMira
        open={globalMiraProps.open}
        output={miraState.response}
        error={miraState.error}
        retryHandler={globalMiraProps.retryHandler}
        buttonText={globalMiraProps.buttonText}
        userInput={globalMiraProps.userInput}
        handleUserInput={handleUserInput}
        onPress={generate}
        onClose={hideMira}
        showCopyToClipboard={globalMiraProps.copyToClipboard}
        customButton={globalMiraProps.customButton}
        showTextArea={globalMiraProps.showTextArea}
        showOutputArea={globalMiraProps.showOutputArea}
        handleOutputChange={handleOutputChange}
      />
    </MiraContext.Provider>
  );
};
